import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
// no icon imports needed on this page
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';
import { useHomes } from '../context/HomesContext';

const Statistics = () => {
  // Use shared homes context
  const { homes, selectedHomeId, setSelectedHomeId, loadHomes: loadHomesContext } = useHomes();
  
  // minimal state for Heating page
  const [error, setError] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [homeModules, setHomeModules] = useState([]); // from homestatus
  const [scale, setScale] = useState('1hour');
  // Only one metric required: sum_boiler_on
  const [types] = useState(['sum_boiler_on']);
  const [measures, setMeasures] = useState(null);

  // Load homes on mount if not already loaded
  useEffect(() => {
    if (homes.length === 0) {
      loadHomesContext();
    }
  }, []);

  const selectedHome = useMemo(() => homes.find(h => h.id === selectedHomeId), [homes, selectedHomeId]);
  // From homestatus: modules include NAPlug and NATherm1
  const devices = useMemo(() => (homeModules || []).filter(m => m.type === 'NAPlug'), [homeModules]);
  const modules = useMemo(() => (homeModules || []).filter(m => m.type === 'NATherm1'), [homeModules]);

  const availableScales = ['30min','1hour','3hours','1day','1week','1month'];

  // No type toggles needed; always use sum_boiler_on

  const fetchMeasures = async () => {
    try {
      setError('');
      setMeasures(null);
      if (!deviceId || !moduleId || types.length === 0) {
        setError('Please select device, module and at least one type');
        return;
      }
      const params = new URLSearchParams();
      params.set('device_id', deviceId);
      params.set('module_id', moduleId);
      params.set('scale', scale);
      types.forEach(t => params.append('type', t));
      params.set('optimize', 'false');
      params.set('real_time', 'false');
      const res = await axios.get(`/api/netatmo/getmeasure?${params.toString()}`);
      setMeasures(res.data.body || {});
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to load measures');
    }
  };

  // Build chart table rows keyed by timestamp; values per type follow the order we requested
  const chartSeries = useMemo(() => {
    if (!measures) return [];
    const keys = Object.keys(measures).sort((a,b) => Number(a) - Number(b));
    return keys.map((ts) => {
      const arr = measures[ts] || [];
      const row = { ts: Number(ts) * 1000 };
      types.forEach((t, idx) => { row[t] = arr[idx]; });
      return row;
    });
  }, [measures, types]);

  // Load homestatus modules when home changes
  useEffect(() => {
    const loadStatus = async () => {
      if (!selectedHomeId) { setHomeModules([]); return; }
      try {
        const res = await axios.get(`/api/netatmo/homestatus/${selectedHomeId}`);
        const mods = res.data?.status?.home?.modules || [];
        setHomeModules(mods);
        // Always reset selection to valid defaults for this home
        const d = mods.find(m => m.type === 'NAPlug');
        setDeviceId(d?.id || '');
        const m = mods.find(mo => mo.type === 'NATherm1');
        setModuleId(m?.id || '');
      } catch (e) {
        console.error(e);
        setError('Failed to load home status');
      }
    };
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHomeId]);

  // Auto-fetch measures when device, module and scale are all available
  useEffect(() => {
    if (deviceId && moduleId && scale && types.length > 0) {
      fetchMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, moduleId, scale]);

  // Do not early-return on error; show inline alert instead

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
        Heating
      </Typography>

      {error && (<Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>)}
  <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Netatmo Boiler Activity
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="home-select">Home</InputLabel>
            <Select labelId="home-select" label="Home" value={selectedHomeId} onChange={(e) => setSelectedHomeId(e.target.value)}>
              {homes.map(h => (
                <MenuItem key={h.id} value={h.id}>{h.alias || h.name || h.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="device-select">Device</InputLabel>
                <Select labelId="device-select" label="Device" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                  {devices.map(d => (
                    <MenuItem key={d.id} value={d.id}>{`${d.id} (${d.type})`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="module-select">Module</InputLabel>
                <Select labelId="module-select" label="Module" value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
                  {modules.map(m => (
                    <MenuItem key={m.id} value={m.id}>{`${m.id} (${m.type})`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="scale-select">Scale</InputLabel>
                <Select labelId="scale-select" label="Scale" value={scale} onChange={(e) => setScale(e.target.value)}>
                  {availableScales.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      {measures && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="ts"
                  tickFormatter={(v) => format(new Date(v), 'PP')}
                  type="number"
                  domain={['dataMin', 'dataMax']}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Boiler on (min)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <Tooltip 
                  labelFormatter={(v) => format(new Date(v), 'PPpp')}
                  formatter={(value, name) => [String(value), name]}
                />
                {types.map((t) => (
                  <Line key={t} type="monotone" dataKey={t} stroke="#e65100" strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
      
    </Container>
  );
};

export default Statistics;